/**
 * @file: parser.ts
 * @author: eric <xuxiang@zhichetech.com>
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

/* tslint:disable:max-line-length */
/* tslint:disable:variable-name */

import { DecodedVinInfo } from './interfaces';

// https://www.doc88.com/p-9425726474883.html
const ISO_REF = 'ISO-3799-2009';

export const REGION_CODE = 'ABCDEFGHJKLMNPRSTUVWXYZ1234567890'.split(''); // with I, O, Q excluded
export const PRODUCTION_YEAR_CODE = 'ABCDEFGHJKLMNPRSTVWXY123456789'.split(''); // with I, O, Q, U, Z, 0 excluded
export const TRANSLITERATING_TABLE = 'ABCDEFGH#JKLMN#P#R#STUVWXYZ'.split('');
export const VIN_DIGIT_WEIGHT_TABLE = [
  8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2,
];

// Vehicle Identification Numbers (VINs) are used to uniquely identify motor vehicles.
// Prior to 1980 there was not an accepted standard for these numbers, so different
// manufacturers used different formats. Modern day VINs consist of 17 characters that
// do not include the letters I, O or Q.
export const VIN_CHAR_REGEX = /^[ABCDEFGHJKLMNPRSTUVWXYZ1234567890]$/;
export const PRODUCTION_YEAR_CHAR_REGEX = /^[ABCDEFGHJKLMNPRSTVWXY123456789]$/;
export const VIN_REGEX = /^[ABCDEFGHJKLMNPRSTUVWXYZ1234567890]{17}$/;
export const CHECK_DIGIT_POS = 8;
export const YEAR_DIGIT_POS = 9;
export const PLANT_CODE_POS = 10;

export const PRODUCTION_YEAR_START = 1980;

export function is_valid_vin(s: string) {
  return VIN_REGEX.test(s);
}
export function is_valid_vin_char(c: string) {
  return VIN_CHAR_REGEX.test(c);
}
export function is_valid_modoel_year_char(c: string) {
  return PRODUCTION_YEAR_CHAR_REGEX.test(c);
}
export function vin_digit_weight(i: number) {
  return VIN_DIGIT_WEIGHT_TABLE[i];
}

export function vin_letter_to_number(c: string) {
  return /^[0-9]$/.test(c)
    ? Number(c)
    : (TRANSLITERATING_TABLE.indexOf(c) % 9) + 1;
}

export function vin_checksum(vin: string) {
  return [...vin].reduce((s, c, i) => {
    s += vin_letter_to_number(c) * vin_digit_weight(i);
    return s;
  }, 0);
}

// https://en.wikibooks.org/wiki/Vehicle_Identification_Numbers_(VIN_codes)/Check_digit
export function vin_checkdigit(vin: string) {
  const checksum = vin_checksum(vin);
  const r = checksum % 11;
  return r === 10 ? 'X' : String(r);
}

export function vin_checked(vin: string) {
  const c = vin_checkdigit(vin);
  if (vin[CHECK_DIGIT_POS] !== c) {
    const vin_chars = [...vin];
    vin_chars.splice(CHECK_DIGIT_POS, 1, c);
    vin = vin_chars.join('');
  }
  return vin;
}

export function vin_is_check_valid(vin: string) {
  const c = vin_checkdigit(vin);
  return vin[CHECK_DIGIT_POS] === c;
}

export function decode_vin_production_year(vin: string) {
  // @ts-ignore
  const [, b] = [vin.charAt(6), vin.charAt(YEAR_DIGIT_POS)];
  const current_year = new Date().getFullYear();
  const i = PRODUCTION_YEAR_CODE.indexOf(b);
  const k = (current_year - 1980) % PRODUCTION_YEAR_CODE.length;
  const [y1, y2] = [1980 + i, 2010 + i];
  return i > k ? y1 : y2;
}

export function vin_decode(vin: string): DecodedVinInfo {
  if (!is_valid_vin(vin)) {
    throw new Error(`VIN ${vin} is invalid: format`);
  }

  const is_valid = vin_is_check_valid(vin);

  const wmi = vin.substring(0, 3);
  const vds = vin.substring(3, 10);
  const vis = vin.substring(9);
  const checksum = vin.charAt(CHECK_DIGIT_POS);
  const production_year = decode_vin_production_year(vin);
  const plant_code = vin.charAt(PLANT_CODE_POS);

  const vpy_threshold = 1000;
  const less_than_vpy_threshold = vin[2] === '9';
  const manufacturer_code = less_than_vpy_threshold ? vin.substr(11, 3) : '';
  const serial_no = less_than_vpy_threshold ? vin.substr(14) : vin.substr(11);

  return {
    vin,
    is_valid,
    iso: { ref: ISO_REF, wmi, vds, vis },
    manufacturer_code,
    production_year,
    plant_code,
    serial_no,
    vpy_threshold,
    less_than_vpy_threshold,
    checksum,
  };
}
